// State for the sectioned "Edit opportunity" modal. The stage choices follow the
// selected pipeline, so moving a deal to another pipeline never submits a stage
// that belongs to the old one. Picking a stage pre-fills its default probability,
// matching what the server applies when only the stage changes.
export default function registerOpportunityEditModal(Alpine) {
  Alpine.data("opportunityEditModal", (config) => opportunityEditModal(config));
}

const toId = (value) => (value === null || value === undefined ? "" : String(value));

const parseNumber = (value) => {
  if (value === null || value === undefined || value === "") return NaN;
  return parseFloat(String(value).replace(/\s/g, "").replace(",", "."));
};

export function opportunityEditModal(config) {
  return {
    section: config.section || "general",
    pipelines: config.pipelines || [],
    // The saved values; after a failed save the current ones are what was submitted.
    originalPipelineId: toId(config.originalPipelineId ?? config.pipelineId),
    originalStageId: toId(config.originalStageId ?? config.stageId),
    originalCustomerId: toId(config.originalCustomerId ?? config.customerId),
    pipelineId: toId(config.pipelineId),
    stageId: toId(config.stageId),
    customerId: toId(config.customerId),
    probability: config.probability ?? "",
    expectedValue: config.expectedValue ?? "",
    currency: config.currency || "NOK",
    locale: config.locale || "en",

    get currentPipeline() {
      return this.pipelines.find((p) => toId(p.id) === this.pipelineId) || null;
    },

    get stages() {
      return this.currentPipeline ? this.currentPipeline.stages : [];
    },

    get currentStage() {
      return this.stages.find((s) => toId(s.id) === this.stageId) || null;
    },

    get pipelineMoved() {
      return this.pipelineId !== this.originalPipelineId;
    },

    get stageChanged() {
      return this.pipelineMoved || this.stageId !== this.originalStageId;
    },

    get customerChanged() {
      return this.customerId !== this.originalCustomerId;
    },

    get closedStageSelected() {
      return !!this.currentStage && this.currentStage.type !== "open";
    },

    get pipelineSummary() {
      const parts = [this.currentPipeline?.name, this.currentStage?.name].filter(Boolean);
      return parts.join(" · ");
    },

    show(key) {
      this.section = key;
    },

    selectPipeline(id) {
      this.pipelineId = toId(id);
      // Only ever land on an open stage: auto-picking Won/Lost would close the
      // deal (and maybe create a contract) on save without an explicit choice.
      const landing = this.stages.find((s) => s.type === "open");
      if (landing) {
        this.selectStage(landing.id);
      } else {
        this.stageId = "";
      }
    },

    selectStage(id) {
      this.stageId = toId(id);
      const stage = this.currentStage;
      if (stage && stage.probability !== null && stage.probability !== undefined) {
        this.probability = String(stage.probability);
      }
    },

    customerPicked(detail) {
      this.customerId = toId(detail?.id);
    },

    weightedValue() {
      const value = parseNumber(this.expectedValue);
      const probability = parseNumber(this.probability);
      if (Number.isNaN(value) || Number.isNaN(probability)) return "–";

      const weighted = (value * probability) / 100;
      try {
        return new Intl.NumberFormat(this.locale, {
          style: "currency",
          currency: this.currency,
          maximumFractionDigits: 0
        }).format(weighted);
      } catch {
        return `${Math.round(weighted)} ${this.currency}`;
      }
    }
  };
}
